import { useState, useRef, useEffect, useCallback } from 'react';
import { previewEmailResponse, suggestIncomingEmail } from '../api';

interface InboxEmail {
  id: string;
  fromName: string;
  fromEmail: string;
  subject: string;
  body: string;
  aiResponse: string;
  timestamp: Date;
  isRead: boolean;
  error: string | null;
}

interface Props {
  chatbotId: string;
  chatbotName: string;
}

const FILLER_EMAILS = [
  { fromName: 'LinkedIn', subject: 'Your weekly newsletter', snippet: 'Top jobs and news for you this week based on your profile...', time: 'Mar 22' },
  { fromName: 'Stripe', subject: 'Your March invoice is ready', snippet: 'Invoice #2847 for $299.00 is now available for download...', time: 'Mar 21' },
  { fromName: 'Google Cloud', subject: 'Security alert', snippet: 'New sign-in to your account from Chrome on Windows...', time: 'Mar 20' },
  { fromName: 'HubSpot', subject: 'Your weekly marketing report', snippet: 'Here are your campaign results for the week of March 17...', time: 'Mar 19' },
  { fromName: 'Notion', subject: 'Weekly digest', snippet: "Here's what changed in your workspace this week...", time: 'Mar 18' },
];

import { formatTime, getInitials } from '../utils/format';

// Auto-resizing textarea
function AutoResizeTextarea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onInput={resize}
      style={{
        width: '100%',
        border: 'none',
        outline: 'none',
        resize: 'none',
        fontSize: '14px',
        lineHeight: 1.7,
        color: 'var(--color-email-text)',
        background: 'transparent',
        fontFamily: 'inherit',
        padding: 0,
        overflow: 'hidden',
        minHeight: '60px',
      }}
    />
  );
}

// ========================
// TOOLBAR (Gmail-style)
// ========================
function Toolbar({ onBack }: { onBack: () => void }) {
  const btnStyle: React.CSSProperties = {
    background: 'none', border: 'none', color: 'var(--color-email-text-secondary)', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', padding: '6px 12px', borderRadius: '4px', whiteSpace: 'nowrap',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 8px', borderBottom: '1px solid var(--color-email-border)', background: 'var(--color-email-surface)', flexWrap: 'wrap' }}>
      <button onClick={onBack} style={{ ...btnStyle, fontWeight: 600 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Back
        </span>
      </button>
      {['Archive', 'Spam', 'Delete'].map(label => (
        <button key={label} style={btnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >{label}</button>
      ))}
      <div style={{ width: '1px', height: '20px', background: 'var(--color-email-divider)', margin: '0 6px' }} />
      {['Mark as unread', 'Move to', 'More'].map(label => (
        <button key={label} style={btnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >{label}</button>
      ))}
    </div>
  );
}

// ========================
// SEARCH BAR
// ========================
function SearchBar() {
  return (
    <div style={{
      padding: '8px 12px',
      background: 'var(--color-email-row-bg)',
      borderBottom: '1px solid var(--color-email-border)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'var(--color-email-search-bg)',
        borderRadius: '24px',
        padding: '6px 16px',
        height: '36px',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span style={{ flex: 1, fontSize: '14px', color: 'var(--color-email-text-secondary)' }}>Search mail</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2">
          <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
          <circle cx="8" cy="6" r="1.5" fill="var(--color-email-text-secondary)" /><circle cx="16" cy="12" r="1.5" fill="var(--color-email-text-secondary)" /><circle cx="10" cy="18" r="1.5" fill="var(--color-email-text-secondary)" />
        </svg>
      </div>
    </div>
  );
}

// ========================
// FORMATTING TOOLBAR (mock RTF)
// ========================
function FormattingToolbar() {
  const iconBtn: React.CSSProperties = {
    background: 'none', border: 'none', color: 'var(--color-email-text-secondary)', cursor: 'default',
    padding: '4px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 8px', borderTop: '1px solid var(--color-email-border)', flexWrap: 'wrap' }}>
      {/* Send button */}
      <button style={{
        background: 'var(--color-email-accent)', color: 'white', border: 'none', borderRadius: '18px',
        padding: '6px 20px', fontSize: '14px', fontWeight: 600, cursor: 'default',
        display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px',
      }}>
        Send
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
      <div style={{ width: '1px', height: '20px', background: 'var(--color-email-divider)', margin: '0 4px' }} />
      {/* Formatting icons */}
      <button style={iconBtn} title="Bold"><strong style={{ fontSize: '15px' }}>B</strong></button>
      <button style={iconBtn} title="Italic"><em style={{ fontSize: '15px' }}>I</em></button>
      <button style={iconBtn} title="Underline"><span style={{ fontSize: '15px', textDecoration: 'underline' }}>U</span></button>
      <div style={{ width: '1px', height: '20px', background: 'var(--color-email-divider)', margin: '0 4px' }} />
      <button style={iconBtn} title="Bulleted list">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" /></svg>
      </button>
      <button style={iconBtn} title="Numbered list">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><text x="2" y="8" fill="currentColor" fontSize="8" fontFamily="inherit" stroke="none">1</text><text x="2" y="14" fill="currentColor" fontSize="8" fontFamily="inherit" stroke="none">2</text><text x="2" y="20" fill="currentColor" fontSize="8" fontFamily="inherit" stroke="none">3</text></svg>
      </button>
      <div style={{ width: '1px', height: '20px', background: 'var(--color-email-divider)', margin: '0 4px' }} />
      <button style={iconBtn} title="Link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
      </button>
      <button style={iconBtn} title="Insert photo">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
      </button>
      <div style={{ flex: 1 }} />
      <button style={iconBtn} title="More options">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>
      </button>
      <button style={iconBtn} title="Discard">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
      </button>
    </div>
  );
}

// ========================
// INBOX ROW
// ========================
function InboxRow({ fromName, subject, snippet, time, unread, onClick }: {
  fromName: string; subject: string; snippet: string; time: string; unread: boolean; onClick?: () => void;
  }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0',
        padding: '0 16px 0 0', height: '40px',
        cursor: onClick ? 'pointer' : 'default',
        background: unread ? 'var(--color-email-unread-bg)' : 'var(--color-email-row-bg)',
        borderLeft: unread ? '3px solid var(--color-email-accent)' : '3px solid transparent',
        borderBottom: '1px solid var(--color-email-border-light)',
        fontSize: '14px',
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.background = unread ? 'var(--color-email-unread-hover)' : 'var(--color-email-hover)'; }}
      onMouseLeave={(e) => { if (onClick) e.currentTarget.style.background = unread ? 'var(--color-email-unread-bg)' : 'var(--color-email-row-bg)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px 0 10px', flexShrink: 0 }}>
        <div style={{ width: '16px', height: '16px', border: '2px solid var(--color-email-text-muted)', borderRadius: '2px', flexShrink: 0 }} />
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-muted)" strokeWidth="1.5" style={{ flexShrink: 0 }}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </div>
      <div className="email-row-sender" style={{ fontWeight: unread ? 700 : 400, color: 'var(--color-email-text)' }}>
        {fromName}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: unread ? 700 : 400, color: 'var(--color-email-text)' }}>{subject}</span>
        <span style={{ color: 'var(--color-email-text-secondary)', fontWeight: 400 }}> &mdash; {snippet}</span>
      </div>
      <div className="email-row-time" style={{ color: unread ? 'var(--color-email-accent)' : 'var(--color-email-text-secondary)', fontWeight: unread ? 700 : 400 }}>
        {time}
      </div>
    </div>
  );
}

// ========================
// FILLER ROW (blurred)
// ========================
function FillerRow({ fromName, subject, snippet, time }: typeof FILLER_EMAILS[0]) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0',
      padding: '0 16px 0 0', height: '40px',
      background: 'var(--color-email-row-bg)', borderLeft: '3px solid transparent', borderBottom: '1px solid var(--color-email-border-light)',
      fontSize: '14px', opacity: 0.4, userSelect: 'none', pointerEvents: 'none', filter: 'blur(0.5px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px 0 10px', flexShrink: 0 }}>
        <div style={{ width: '16px', height: '16px', border: '2px solid var(--color-email-text-muted)', borderRadius: '2px' }} />
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-muted)" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </div>
      <div className="email-row-sender" style={{ color: 'var(--color-email-text)' }}>
        {fromName}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ color: 'var(--color-email-text)' }}>{subject}</span>
        <span style={{ color: 'var(--color-email-text-secondary)' }}> &mdash; {snippet}</span>
      </div>
      <div className="email-row-time" style={{ color: 'var(--color-email-text-secondary)' }}>
        {time}
      </div>
    </div>
  );
}

// ========================
// MAIN COMPONENT
// ========================
export default function EmailResponderInbox({ chatbotId }: Props) {
  const [fromName, setFromName] = useState('John Doe');
  const [fromEmail, setFromEmail] = useState('john.doe@gmail.com');
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emails, setEmails] = useState<InboxEmail[]>([]);
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(true);
  const [loading, setLoading] = useState(false);
  const [draftGlow, setDraftGlow] = useState(false);
  const [draftTexts, setDraftTexts] = useState<Record<string, string>>({});
  const [senderExpanded, setSenderExpanded] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const pendingRef = useRef<{ id: string; fromName: string; fromEmail: string; subject: string; body: string } | null>(null);

  const openEmail = openEmailId ? emails.find(e => e.id === openEmailId) : null;

  useEffect(() => {
    if (openEmailId) {
      setDraftGlow(true);
      const timer = setTimeout(() => setDraftGlow(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [openEmailId]);

  async function handleSuggestAndSend() {
    if (suggesting || loading) return;
    setSuggesting(true);
    try {
      const result = await suggestIncomingEmail(chatbotId);
      if (result.success && result.data) {
        // Feed directly into send flow
        setSuggesting(false);
        sendEmail({
          fromName: fromName.trim() || 'John Doe',
          fromEmail: fromEmail.trim() || 'john.doe@gmail.com',
          subject: result.data.subject,
          body: result.data.body,
        });
      } else {
        setSuggesting(false);
      }
    } catch {
      setSuggesting(false);
    }
  }

  async function handleSend() {
    if (!emailBody.trim() || loading) return;
    sendEmail({
      fromName: fromName.trim() || 'John Doe',
      fromEmail: fromEmail.trim() || 'john.doe@gmail.com',
      subject: subject.trim() || '(no subject)',
      body: emailBody.trim(),
    });
  }

  async function sendEmail(fields: { fromName: string; fromEmail: string; subject: string; body: string }) {
    const id = crypto.randomUUID();
    const pending = { id, ...fields };
    pendingRef.current = pending;

    setFromName('John Doe');
    setFromEmail('john.doe@gmail.com');
    setSubject('');
    setEmailBody('');
    setShowCompose(false);
    setSenderExpanded(false);
    setLoading(true);

    try {
      const result = await previewEmailResponse(chatbotId, pending.body, {
        fromName: pending.fromName,
        fromEmail: pending.fromEmail,
        subject: pending.subject,
      });

      const aiResponse = result.success && result.data ? result.data.message : '';
      const newEmail: InboxEmail = {
        id: pending.id, fromName: pending.fromName, fromEmail: pending.fromEmail,
        subject: pending.subject, body: pending.body, aiResponse,
        timestamp: new Date(), isRead: false,
        error: !result.success ? (result.error || 'Failed to generate response') : null,
      };

      setEmails(prev => [newEmail, ...prev]);
      setDraftTexts(prev => ({ ...prev, [pending.id]: aiResponse }));
      setOpenEmailId(null); // Show inbox first
    } catch {
      const newEmail: InboxEmail = {
        id: pending.id, fromName: pending.fromName, fromEmail: pending.fromEmail,
        subject: pending.subject, body: pending.body, aiResponse: '',
        timestamp: new Date(), isRead: false,
        error: 'An error occurred while generating the response',
      };
      setEmails(prev => [newEmail, ...prev]);
    } finally {
      setLoading(false);
      pendingRef.current = null;
    }
  }

  function handleOpenEmail(emailId: string) {
    setOpenEmailId(emailId);
    setEmails(prev => prev.map(e =>
      e.id === emailId ? { ...e, isRead: true } : e
    ));
  }

  function handleBack() {
    setOpenEmailId(null);
  }

  // ========================
  // EMAIL DETAIL VIEW (Gmail-style)
  // ========================
  if (openEmail) {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Search bar */}
        <SearchBar />

        {/* Toolbar */}
        <Toolbar onBack={handleBack} />

        {/* Email content */}
        <div style={{ padding: '24px 28px 0' }}>
          {/* Subject + Inbox label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 400, color: 'var(--color-email-text)', margin: 0, lineHeight: 1.3 }}>
              {openEmail.subject}
            </h2>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              background: 'var(--color-email-tag-bg)', borderRadius: '4px', padding: '2px 8px',
              fontSize: '12px', color: 'var(--color-email-text-secondary)', fontWeight: 500, flexShrink: 0,
            }}>
              Inbox &times;
            </span>
          </div>

          {/* Sender info */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%', background: 'var(--color-email-accent)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 500,
              flexShrink: 0, marginTop: '2px',
            }}>
              {getInitials(openEmail.fromName)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-email-text)' }}>{openEmail.fromName}</span>
                <span style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)' }}>&lt;{openEmail.fromEmail}&gt;</span>
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--color-email-text-secondary)', flexShrink: 0 }}>
                  {openEmail.timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)', marginTop: '1px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                to me
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2" style={{ marginTop: '1px' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          </div>

          {/* Email body */}
          <div className="email-detail-body" style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--color-email-text)', whiteSpace: 'pre-wrap', paddingBottom: '24px' }}>
            {openEmail.body}
          </div>
        </div>

        {/* AI Draft Reply Box */}
        <div className="email-draft-reply" style={{
          marginRight: '28px',
          marginBottom: '24px',
          borderRadius: '12px',
          border: '1px solid var(--color-email-draft-border)',
          background: 'var(--color-email-row-bg)',
          overflow: 'hidden',
          animation: draftGlow ? 'draftGlow 1.5s ease-out' : 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          {openEmail.error ? (
            <div style={{ padding: '16px 20px', color: 'var(--color-danger)', fontSize: '14px' }}>
              {openEmail.error}
            </div>
          ) : (
            <>
              {/* Draft header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-email-tag-bg)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: 'var(--color-email-accent)', color: 'white',
                  padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M12 2L9 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5L22 9h-7z" />
                  </svg>
                  Draft Ready
                </div>
                <span style={{ fontSize: '12px', color: 'var(--color-email-text-secondary)' }}>AI-generated draft based on your knowledge base</span>
              </div>

              {/* Reply header line */}
              <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2" style={{ transform: 'scaleX(-1)' }}>
                  <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                <span style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)' }}>{openEmail.fromName} &lt;{openEmail.fromEmail}&gt;</span>
              </div>

              {/* Draft body */}
              <div style={{ padding: '12px 16px' }}>
                <AutoResizeTextarea
                  value={draftTexts[openEmail.id] ?? openEmail.aiResponse}
                  onChange={(v) => setDraftTexts(prev => ({ ...prev, [openEmail.id]: v }))}
                />
              </div>

              {/* Formatting toolbar + Send */}
              <FormattingToolbar />
            </>
          )}
        </div>
      </div>
    );
  }

  // ========================
  // INBOX VIEW
  // ========================
  const hasResults = emails.length > 0;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--color-email-border)' }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Test Email Responder</h3>
          <p style={{ fontSize: '14px', color: 'var(--color-email-text-secondary)', margin: '4px 0 0' }}>Simulate an incoming email and see how the AI drafts a response</p>
        </div>
        {!loading && (
          <button
            onClick={() => setShowCompose(!showCompose)}
            className="btn btn-primary btn-small"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Compose
          </button>
        )}
      </div>

      {/* Compose Form */}
      {showCompose && !loading && (
        <div style={{ background: 'var(--color-email-surface)', borderBottom: '1px solid var(--color-email-border)', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--color-email-text)' }}>New incoming email</div>
          {/* Collapsible sender fields */}
          <div style={{ marginBottom: '12px' }}>
            {!senderExpanded ? (
              <div
                onClick={() => setSenderExpanded(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-email-border)',
                  cursor: 'pointer', background: 'var(--color-email-row-bg)', fontSize: '14px',
                  transition: 'border-color 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-email-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-email-border)'; }}
              >
                <span style={{ color: 'var(--color-email-text-secondary)', fontSize: '13px', fontWeight: 500 }}>From:</span>
                <span style={{ color: 'var(--color-email-text)', fontWeight: 500 }}>{fromName || 'John Doe'}</span>
                <span style={{ color: 'var(--color-email-text-secondary)' }}>&lt;{fromEmail || 'john.doe@gmail.com'}&gt;</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
            ) : (
              <div className="grid-2col" style={{ gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-email-text-secondary)', display: 'block', marginBottom: '4px' }}>From Name</label>
                  <input type="text" className="input" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="John Doe" />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-email-text-secondary)', display: 'block', marginBottom: '4px' }}>From Email</label>
                  <input type="email" className="input" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="john.doe@gmail.com" />
                </div>
              </div>
            )}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-email-text-secondary)', display: 'block', marginBottom: '4px' }}>Subject</label>
            <input type="text" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Product inquiry" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-email-text-secondary)', display: 'block', marginBottom: '4px' }}>Email Body <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <textarea className="input textarea" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={5} placeholder="Type or paste a sample incoming email..." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={handleSuggestAndSend}
              disabled={suggesting || loading}
              style={{
                background: 'none', border: 'none', color: 'var(--color-email-text-secondary)', cursor: 'pointer',
                fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px',
                padding: '4px 8px', borderRadius: '6px',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-email-accent)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-email-text-secondary)'; }}
            >
              {suggesting ? (
                <><span className="spinner spinner-sm" style={{ width: '14px', height: '14px' }} /> Generating...</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L9 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5L22 9h-7z" />
                  </svg>
                  Suggest an email
                </>
              )}
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowCompose(false)} className="btn btn-secondary btn-small">Cancel</button>
              <button onClick={handleSend} className="btn btn-primary btn-small" disabled={!emailBody.trim()} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 20px', borderBottom: '1px solid var(--color-email-border)' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ fontSize: '15px', color: 'var(--color-email-text-secondary)', margin: 0, fontWeight: 500 }}>Generating AI draft response...</p>
        </div>
      )}

      {/* Empty state hint */}
      {!loading && !showCompose && !hasResults && (
        <div style={{ textAlign: 'center', padding: '32px 20px', background: 'var(--color-email-surface)' }}>
          <p style={{ fontSize: '14px', color: 'var(--color-email-text-secondary)', margin: 0 }}>
            Click <strong>Compose</strong> to simulate an incoming email and see the AI draft a response
          </p>
        </div>
      )}

      {/* Inbox rows — only visible once we have results */}
      {!loading && hasResults && (
        <>
          <SearchBar />
          {emails.map((email) => (
            <InboxRow
              key={email.id}
              fromName={email.fromName}
              subject={email.subject}
              snippet={email.body.substring(0, 80)}
              time={formatTime(email.timestamp)}
              unread={!email.isRead}
              onClick={() => handleOpenEmail(email.id)}
            />
          ))}
          {FILLER_EMAILS.map((filler, i) => (
            <FillerRow key={i} {...filler} />
          ))}
        </>
      )}
    </div>
  );
}
